import React, { useState } from "react";
import { Trees, CalendarPlus, Check, X, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { amenidades as apiAmenidades, reservas as apiReservas } from "../../api/modulos.service";
import { estilos as est, Cabecera, Vacio, Aviso, Ventana, Entrada, Seleccion, Area, useCarga } from "./base";

/**
 * AMENIDADES Y RESERVAS.
 *
 * Una sola pantalla con las dos cosas porque se usan juntas: el residente ve
 * el parque y, desde ahí, lo reserva. Separarlas obligaría a ir y volver para
 * comprobar si está libre.
 *
 * Las reservas aprobadas las ve TODA la comunidad, y ésa es la mitad del
 * valor: que nadie llegue el sábado creyendo que el salón está libre.
 */

const FORM_AMENIDAD = { name: "", description: "", capacity: "", maxHours: 4, openTime: "06:00", closeTime: "22:00", rules: "", requiresApproval: true };
const FORM_RESERVA = { amenity: "", fecha: "", desde: "16:00", hasta: "18:00", people: 1, purpose: "" };

const COLOR = { pendiente: "var(--warning)", aprobada: "var(--success)", rechazada: "var(--danger)", cancelada: "var(--text-tertiary)" };

const cuando = (r) =>
  `${new Date(r.startAt).toLocaleDateString("es-SV", { timeZone: "America/El_Salvador", dateStyle: "medium" })} · ` +
  `${new Date(r.startAt).toLocaleTimeString("es-SV", { timeZone: "America/El_Salvador", hour: "2-digit", minute: "2-digit" })}–` +
  `${new Date(r.endAt).toLocaleTimeString("es-SV", { timeZone: "America/El_Salvador", hour: "2-digit", minute: "2-digit" })}`;

export default function Amenidades({ rol }) {
  const [ventanaAmenidad, setVentanaAmenidad] = useState(false);
  const [ventanaReserva, setVentanaReserva] = useState(false);
  const [formA, setFormA] = useState(FORM_AMENIDAD);
  const [formR, setFormR] = useState(FORM_RESERVA);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const amenidades = useCarga(() => apiAmenidades.listar({}), []);
  const reservas = useCarga(() => apiReservas.listar({}), []);
  /* Si cualquiera de las dos cargas falla, se dice. Sin esto la pantalla
   * enseña «no hay amenidades», que no es lo mismo que «no se pudo cargar». */
  const errorCarga = amenidades.error || reservas.error;

  const esAdmin = rol === "admin";
  const lista = amenidades.datos?.amenities || [];
  const listaReservas = reservas.datos?.reservations || [];

  const cambiarA = (c) => (e) => setFormA({ ...formA, [c]: e.target.value });
  const cambiarR = (c) => (e) => setFormR({ ...formR, [c]: e.target.value });

  const guardarAmenidad = async (e) => {
    e.preventDefault();
    setError(""); setGuardando(true);
    try {
      await apiAmenidades.crear({ ...formA, capacity: Number(formA.capacity) || 0, maxHours: Number(formA.maxHours) || 4 });
      setVentanaAmenidad(false); setFormA(FORM_AMENIDAD); amenidades.recargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo crear la amenidad");
    } finally { setGuardando(false); }
  };

  /* La hora se convierte a UTC desde El Salvador (UTC-6, sin horario de
     verano). Mandar la hora local tal cual haría que una reserva de las
     cuatro de la tarde se guardara como si fueran las diez de la mañana. */
  const aUtc = (fecha, hora) => new Date(`${fecha}T${hora}:00-06:00`).toISOString();

  const guardarReserva = async (e) => {
    e.preventDefault();
    setError(""); setGuardando(true);
    try {
      await apiReservas.crear({
        amenity: formR.amenity,
        startAt: aUtc(formR.fecha, formR.desde),
        endAt: aUtc(formR.fecha, formR.hasta),
        people: Number(formR.people) || 1,
        purpose: formR.purpose,
      });
      setVentanaReserva(false); setFormR(FORM_RESERVA); reservas.recargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo reservar");
    } finally { setGuardando(false); }
  };

  /* El error corta ANTES de pintar: sin esto la pantalla enseñaba la rejilla
   * de amenidades vacía y «No hay reservas.», que se lee como que no hay nada
   * reservado cuando en realidad no se pudo preguntar. El botón reintenta las
   * dos cargas, porque cualquiera de las dos pudo ser la que falló. */
  if (errorCarga)
    return (
      <div style={est.seccion}>
        <Cabecera
          titulo="Amenidades"
          subtitulo="El parque, la piscina, el salón."
          icono={esAdmin ? Trees : CalendarPlus}
        />
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button
              size="sm"
              variant="outline"
              leftIcon={RefreshCw}
              onClick={() => {
                amenidades.recargar();
                reservas.recargar();
              }}
            >
              Reintentar
            </Button>
          </div>
        </Aviso>
      </div>
    );

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Amenidades"
        subtitulo="El parque, la piscina, el salón. Reserve y vea lo que ya está ocupado."
        accion={esAdmin ? "Crear amenidad" : "Reservar"}
        alAccionar={() => (esAdmin ? setVentanaAmenidad(true) : setVentanaReserva(true))}
        icono={esAdmin ? Trees : CalendarPlus}
      />

      {error && <Aviso>{error}</Aviso>}

      <div style={est.rejilla}>
        {lista.map((a) => (
          <div key={a._id} style={est.tarjeta}>
            <h3 style={est.tarjetaTitulo}>{a.name}</h3>
            {a.description && <div style={est.tarjetaDato}>{a.description}</div>}
            <div style={est.tarjetaDato}>
              {a.openTime}–{a.closeTime} · máximo {a.maxHours} h
              {a.capacity > 0 ? ` · hasta ${a.capacity} personas` : ""}
            </div>
            {a.rules && <div style={{ ...est.tarjetaDato, fontStyle: "italic" }}>{a.rules}</div>}
            {!esAdmin && (
              <div style={est.tarjetaAcciones}>
                <Button size="sm" variant="primary" leftIcon={CalendarPlus}
                  onClick={() => { setFormR({ ...FORM_RESERVA, amenity: a._id }); setVentanaReserva(true); }}>
                  Reservar
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <h3 style={{ ...est.titulo, fontSize: "var(--text-lg)" }}>Calendario de reservas</h3>
      {listaReservas.length === 0 ? (
        <Vacio>No hay reservas.</Vacio>
      ) : (
        <div style={est.rejilla}>
          {listaReservas.map((r) => (
            <div key={r._id} style={{ ...est.tarjeta, borderColor: COLOR[r.status] }}>
              <h3 style={est.tarjetaTitulo}>{r.amenity?.name}</h3>
              <div style={est.tarjetaDato}>{cuando(r)}</div>
              <div style={est.tarjetaDato}>
                Casa {r.polygon}-{r.houseNumber} · {r.people} persona(s)
              </div>
              {r.purpose && <div style={est.tarjetaDato}>{r.purpose}</div>}
              <div style={{ ...est.tarjetaDato, color: COLOR[r.status], fontWeight: 600 }}>
                {r.status}
              </div>

              <div style={est.tarjetaAcciones}>
                {esAdmin && r.status === "pendiente" && (
                  <>
                    <Button size="sm" variant="primary" leftIcon={Check}
                      onClick={() => apiReservas.decidir(r._id, { decision: "aprobada" }).then(reservas.recargar)}>
                      Aprobar
                    </Button>
                    <Button size="sm" variant="danger" leftIcon={X}
                      onClick={() => apiReservas.decidir(r._id, { decision: "rechazada" }).then(reservas.recargar)}>
                      Rechazar
                    </Button>
                  </>
                )}
                {r.status !== "cancelada" && r.status !== "rechazada" && (
                  <Button size="sm" variant="ghost"
                    onClick={() => apiReservas.cancelar(r._id).then(reservas.recargar)}>
                    Cancelar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo="Crear una amenidad"
        abierta={ventanaAmenidad}
        alCerrar={() => setVentanaAmenidad(false)}
        pie={<>
          <Button variant="ghost" onClick={() => setVentanaAmenidad(false)}>Cancelar</Button>
          <Button variant="primary" onClick={guardarAmenidad} loading={guardando}>Crear</Button>
        </>}
      >
        {error && <Aviso>{error}</Aviso>}
        <Entrada etiqueta="Nombre" value={formA.name} onChange={cambiarA("name")} placeholder="Parque 3" required />
        <Area etiqueta="Descripción" value={formA.description} onChange={cambiarA("description")} />
        <div style={est.fila}>
          <Entrada etiqueta="Capacidad" type="number" value={formA.capacity} onChange={cambiarA("capacity")} placeholder="0 = sin límite" />
          <Entrada etiqueta="Horas máximas" type="number" value={formA.maxHours} onChange={cambiarA("maxHours")} />
        </div>
        <div style={est.fila}>
          <Entrada etiqueta="Abre" type="time" value={formA.openTime} onChange={cambiarA("openTime")} />
          <Entrada etiqueta="Cierra" type="time" value={formA.closeTime} onChange={cambiarA("closeTime")} />
        </div>
        <Area etiqueta="Reglas" value={formA.rules} onChange={cambiarA("rules")} />
      </Ventana>

      <Ventana
        titulo="Reservar"
        abierta={ventanaReserva}
        alCerrar={() => setVentanaReserva(false)}
        pie={<>
          <Button variant="ghost" onClick={() => setVentanaReserva(false)}>Cancelar</Button>
          <Button variant="primary" onClick={guardarReserva} loading={guardando}>Reservar</Button>
        </>}
      >
        {error && <Aviso>{error}</Aviso>}
        <Seleccion
          etiqueta="Amenidad"
          value={formR.amenity}
          onChange={cambiarR("amenity")}
          opciones={[{ valor: "", etiqueta: "Elija una" }, ...lista.map((a) => ({ valor: a._id, etiqueta: a.name }))]}
        />
        <Entrada etiqueta="Fecha" type="date" value={formR.fecha} onChange={cambiarR("fecha")} required />
        <div style={est.fila}>
          <Entrada etiqueta="Desde" type="time" value={formR.desde} onChange={cambiarR("desde")} />
          <Entrada etiqueta="Hasta" type="time" value={formR.hasta} onChange={cambiarR("hasta")} />
          <Entrada etiqueta="Personas" type="number" value={formR.people} onChange={cambiarR("people")} />
        </div>
        <Area etiqueta="Para qué" value={formR.purpose} onChange={cambiarR("purpose")} placeholder="Cumpleaños" />
      </Ventana>
    </div>
  );
}
